import { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import { PersonAdd, Login, ContactMail } from '@mui/icons-material';

import { useTranslation } from '../hooks/useTranslation';

import { RegistrationForm } from '../components/forms/RegistrationForm';
import { LoginForm } from '../components/forms/LoginForm';
import { ContactForm } from '../components/forms/ContactForm';
import {
  UserFormData,
  LoginFormData,
  ContactFormData,
} from '../utils/validation';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`forms-tabpanel-${index}`}
      aria-labelledby={`forms-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

export const FormsPage = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [success, setSuccess] = useState<string | undefined>(undefined);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(undefined);
    setSuccess(undefined);
  };

  const showSuccess = (message: string) => {
    setSuccess(message);
    setError(undefined);
  };

  const showError = (message: string) => {
    setError(message);
    setSuccess(undefined);
  };

  const handleRegistration = async (data: UserFormData) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('Registration data:', data);
      showSuccess(t('auth.registration.success'));
    } catch (_err) {
      showError(t('auth.registration.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (data: LoginFormData) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('Login data:', data);
      showSuccess(t('auth.login.success'));
    } catch (_err) {
      showError(t('auth.login.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleContact = async (data: ContactFormData) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Contact data:', data);
      showSuccess(t('contact.success'));
    } catch (_err) {
      showError(t('contact.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    showSuccess(t('auth.login.forgotPasswordSuccess'));
  };

  const handleRegister = () => {
    setTabValue(0);
  };

  return (
    <Box>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        {t('forms.title')}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        align="center"
        paragraph
      >
        {t('forms.subtitle')}
      </Typography>

      <Paper sx={{ mt: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="forms tabs"
            centered
          >
            <Tab
              icon={<PersonAdd />}
              label={t('forms.tabs.registration')}
              id="forms-tab-0"
              aria-controls="forms-tabpanel-0"
            />
            <Tab
              icon={<Login />}
              label={t('forms.tabs.login')}
              id="forms-tab-1"
              aria-controls="forms-tabpanel-1"
            />
            <Tab
              icon={<ContactMail />}
              label={t('forms.tabs.contact')}
              id="forms-tab-2"
              aria-controls="forms-tabpanel-2"
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <RegistrationForm
            onSubmit={handleRegistration}
            loading={loading}
            error={error}
            success={success}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <LoginForm
            onSubmit={handleLogin}
            loading={loading}
            error={error}
            onForgotPassword={handleForgotPassword}
            onRegister={handleRegister}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <ContactForm
            onSubmit={handleContact}
            loading={loading}
            error={error}
            success={success}
          />
        </TabPanel>
      </Paper>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(undefined)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          variant="filled"
          onClose={() => setSuccess(undefined)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};
